import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminService } from '../../services/adminService';
import { useAuth } from '../../contexts/AuthContext';
import CustomerManagement from './CustomerManagement';
import MSPManagement from './MSPManagement';
import InviteCodeManagement from './InviteCodeManagement';
import { UsersIcon, BuildingOfficeIcon, TicketIcon, ArrowLeftOnRectangleIcon } from '@heroicons/react/24/outline';

const AdminDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'customers' | 'msps' | 'invites'>('customers');
  const navigate = useNavigate();
  const { setUserDirectly } = useAuth();

  useEffect(() => {
    // Check if admin is authenticated
    if (!adminService.isAdmin()) {
      navigate('/admin/login');
    }
  }, [navigate]);

  const handleLogout = () => {
    adminService.logout();
    navigate('/admin/login');
  };

  const handleLoginAsUser = async (user: any) => {
    try {
      const authResponse = await adminService.loginAsUser(user.id);
      setUserDirectly(authResponse.user);
      window.location.href = '/dashboard';
    } catch (err: any) {
      console.error('Failed to login as user:', err);
    }
  };

  const tabs = [
    { id: 'customers', name: 'Customers', icon: UsersIcon },
    { id: 'msps', name: 'MSPs', icon: BuildingOfficeIcon },
    { id: 'invites', name: 'Invite Codes', icon: TicketIcon },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Admin Dashboard
              </h1>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                Manage customers, MSPs, and invite codes
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700"
            >
              <ArrowLeftOnRectangleIcon className="h-4 w-4 mr-2" />
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`
                    group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
                    ${
                      activeTab === tab.id
                        ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                    }
                  `}
                >
                  <Icon
                    className={`
                      -ml-0.5 mr-2 h-5 w-5
                      ${
                        activeTab === tab.id
                          ? 'text-indigo-500 dark:text-indigo-400'
                          : 'text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300'
                      }
                    `}
                  />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'customers' && (
            <CustomerManagement onLoginAsUser={handleLoginAsUser} />
          )}
          {activeTab === 'msps' && <MSPManagement />}
          {activeTab === 'invites' && <InviteCodeManagement />}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;